import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, FileText, Wallet, Flag } from "lucide-react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import FloatingBookCTA from "@/components/site/FloatingBookCTA";
import usePageMeta from "@/lib/use-page-meta";

const CATEGORIES = [
  {
    label: "NLV planning tools",
    sub: "For UK and other non-EU citizens using the Non-Lucrative Visa route.",
    tools: [
      {
        to: "/quiz",
        icon: Sparkles,
        eyebrow: "2-minute quiz",
        title: "Am I NLV-ready?",
        body: "7 short questions. Get a personalised readiness score and a clear next step — whether that's booking a call or doing your homework first.",
        testid: "tools-card-quiz",
      },
      {
        to: "/checklist",
        icon: FileText,
        eyebrow: "Free download",
        title: "The NLV Readiness Checklist",
        body: "Every document, deadline and form in one printable page. Tick it off as you go and never miss a step.",
        testid: "tools-card-checklist",
      },
      {
        to: "/calculator",
        icon: Wallet,
        eyebrow: "Live estimator",
        title: "What will it actually cost?",
        body: "A personalised cost range built from real applicant data — visa fees, documents, health insurance, the lot.",
        testid: "tools-card-calculator",
      },
    ],
  },
  {
    label: "EU passport planning tools",
    sub: "For people moving with an EU, EEA or Swiss passport — or an EU spouse or partner.",
    tools: [
      {
        to: "/eu-readiness",
        icon: Flag,
        eyebrow: "2-minute check",
        title: "The EU Passport Readiness Checklist",
        body: "Your passport opens the door — this tells you how ready the rest of your move is. Personalised score and next step included.",
        testid: "tools-card-eu-readiness",
      },
    ],
  },
];

export default function Tools() {
  usePageMeta(
    "Free Tools for Planning Your Move to Spain | Get to Spain",
    "Use free Get to Spain quizzes, checklists and calculators to understand your route, readiness, costs and next steps."
  );
  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <SiteNav />

      <section className="pt-28 pb-20 lg:pt-32 lg:pb-24 editorial relative overflow-hidden">
        <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full bg-[var(--terracotta)]/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 -left-32 w-[400px] h-[400px] rounded-full bg-[var(--gold)]/15 blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)]" data-testid="tools-back">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          <div className="mt-8 max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">
              Free tools
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--navy)] mt-5 leading-[1.04]" data-testid="tools-headline">
              Free tools to <em className="text-[var(--terracotta)]">work out where you are.</em>
            </h1>
            <p className="mt-6 text-lg text-[var(--ink-soft)] leading-relaxed">
              Simple, effective tools to help with your planning — organised by route. Pick
              whichever you need first; they all link to the others when you're done.
            </p>
          </div>

          {CATEGORIES.map((cat, ci) => (
            <div key={ci} className="mt-14" data-testid={`tools-category-${ci}`}>
              <div className="flex items-baseline gap-4 flex-wrap">
                <h2 className="font-serif text-2xl lg:text-3xl text-[var(--navy)]">{cat.label}</h2>
                <span className="text-sm text-[var(--muted)]">{cat.sub}</span>
              </div>
              <div className="mt-6 grid md:grid-cols-2 gap-5">
                {cat.tools.map((t, i) => (
                  <Link
                    to={t.to}
                    key={i}
                    data-testid={t.testid}
                    className="group rounded-3xl bg-white p-7 lg:p-8 border border-black/5 hover:border-[var(--terracotta)]/30 transition-colors flex flex-col"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-[var(--terracotta)]/10 text-[var(--terracotta)] flex items-center justify-center group-hover:bg-[var(--terracotta)] group-hover:text-[var(--cream)] transition-colors">
                      <t.icon className="w-5 h-5" />
                    </div>
                    <div className="text-xs uppercase tracking-[0.25em] text-[var(--muted)] font-medium mt-6">{t.eyebrow}</div>
                    <h3 className="mt-3 font-serif text-2xl lg:text-3xl text-[var(--navy)] leading-tight">{t.title}</h3>
                    <p className="mt-4 text-[var(--ink-soft)] leading-relaxed text-[0.95rem] flex-1">{t.body}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--terracotta)] font-medium group-hover:gap-3 transition-all">
                      Open <ArrowRight className="w-4 h-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <SiteFooter />
      <FloatingBookCTA />
    </main>
  );
}
